'use client';

import { type AxiosError, HttpStatusCode, isAxiosError } from 'axios';
import { usePathname, useRouter } from 'next/navigation';
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { AuthContext, type PartnerKeycloakCurrentUser, type VerifyCredentialsParams } from '@/contexts/auth-context';
import { API_ROUTES } from '@/shared/constants/api-routes';
import { APP_ROUTES } from '@/shared/constants/app-routes';
import type { Role } from '@/shared/constants/roles';
import { SESSION_EXPIRED_QUERY_PARAM, SESSION_EXPIRED_QUERY_VALUE } from '@/shared/constants/session-expired-query';
import { buildApiRoute } from '@/shared/functions/build-api-route';
import { buildAppRoute } from '@/shared/functions/build-app-route';
import { isPublicRoute } from '@/shared/functions/is-public-route';
import { handleErrorTreatment } from '@/shared/functions/zod/get-zod-errors';
import { api, authless } from '../axios/api';
export interface RefreshPartnerTokenResponse {
  partnerUser: PartnerKeycloakCurrentUser;
  isFirstLogin: boolean;
}
type QueuedRequest = {
  resolve: (value?: unknown) => void;
  reject: (error?: unknown) => void;
};
class RefreshTokenQueue {
  private isRefreshing = false;
  private queue: QueuedRequest[] = [];
  isInProgress(): boolean {
    return this.isRefreshing;
  }
  enqueue(request: QueuedRequest): void {
    this.queue.push(request);
  }
  flush(error?: unknown): void {
    for (const queuedRequest of this.queue) {
      if (error) {
        queuedRequest.reject(error);
      } else {
        queuedRequest.resolve();
      }
    }
    this.queue = [];
  }
  setRefreshing(value: boolean): void {
    this.isRefreshing = value;
  }
}
const refreshTokenQueue = new RefreshTokenQueue();
function isAuthEndpoint(url?: string): boolean {
  if (!url) return false;
  return url.includes(API_ROUTES.POST.AUTH.REFRESH_TOKEN) || url.includes(API_ROUTES.GET.AUTH.GET_LOGOUT_URL);
}
function redirectToLogin(push: (url: string) => void): void {
  push(buildAppRoute(APP_ROUTES.PUBLIC.LOGIN));
}

/**
 * Login sinalizando sessão morta, para o proxy não devolver o usuário à rota privada.
 * Ver {@link SESSION_EXPIRED_QUERY_PARAM}.
 */
function redirectToExpiredSessionLogin(push: (url: string) => void): void {
  push(buildAppRoute(APP_ROUTES.PUBLIC.LOGIN, {
    [SESSION_EXPIRED_QUERY_PARAM]: SESSION_EXPIRED_QUERY_VALUE
  }));
}
export const AuthProvider = ({
  children
}: {
  children: ReactNode;
}) => {
  const pathname = usePathname();
  const sessionTerminatedRef = useRef(false);
  const {
    push
  } = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const [userId, setUserId] = useState<PartnerKeycloakCurrentUser['id'] | undefined>(undefined);
  const [userRoles, setUserRoles] = useState<Array<Role>>([]);
  const updateUserSession = useCallback(async (user: PartnerKeycloakCurrentUser) => {
    setUserRoles(user.roles);
    setUserId(user.id);
  }, []);

  /**
   * Derruba a sessão local e manda o usuário para o login.
   *
   * O GET da url de logout é chamado pelo efeito colateral: com a sessão já morta no
   * backend, o guard responde 401 com `Set-Cookie` expirando o cookie de sessão. Sem
   * essa chamada o cookie sobrevive no browser e o proxy continua tratando o usuário
   * como autenticado.
   */
  const terminateSession = useCallback(async (message?: string) => {
    sessionTerminatedRef.current = true;
    setUserId(undefined);
    setUserRoles([]);
    if (message) toast.error(message);
    await authless.get<string>(buildApiRoute(API_ROUTES.GET.AUTH.GET_LOGOUT_URL)).catch(() => {});
    redirectToExpiredSessionLogin(push);
  }, [push]);
  const refresh = useCallback(async () => {
    if (isPublicRoute(pathname) || sessionTerminatedRef.current) return;
    setIsLoading(true);
    try {
      const {
        data: {
          partnerUser
        }
      } = await authless.post<RefreshPartnerTokenResponse>(buildApiRoute(API_ROUTES.POST.AUTH.REFRESH_TOKEN));
      await updateUserSession(partnerUser);
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === HttpStatusCode.Unauthorized) {
        await terminateSession('Sessão expirada');
        return;
      }
      handleErrorTreatment(err);
      await terminateSession();
    } finally {
      setIsLoading(false);
    }
  }, [updateUserSession, terminateSession, pathname]);
  const getLogoutUrl = useCallback(async () => {
    setIsLoading(true);
    try {
      const {
        data: {
          url
        }
      } = await api.get<{
        url: string;
      }>(buildApiRoute(API_ROUTES.GET.AUTH.GET_LOGOUT_URL));
      window.open(url, '_self', 'noopener,noreferrer')?.focus();
    } catch (err_0) {
      handleErrorTreatment(err_0);
    } finally {
      setIsLoading(false);
    }
  }, []);
  const getLoginUrl = useCallback(async () => {
    setIsLoading(true);
    try {
      const {
        data: {
          url: url_0
        }
      } = await authless.get<{
        url: string;
      }>(buildApiRoute(API_ROUTES.GET.AUTH.GET_LOGIN_URL));
      window.open(url_0, '_self', 'noopener,noreferrer')?.focus();
    } catch (err_1) {
      handleErrorTreatment(err_1);
    } finally {
      setIsLoading(false);
    }
  }, []);
  const createErrorInterceptor = useCallback(() => api.interceptors.response.use(response => response, async (error: AxiosError) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    if (!originalRequest) {
      return Promise.reject(error);
    }
    const shouldNotRetry = status !== HttpStatusCode.Unauthorized || (originalRequest as {
      _retry?: boolean;
    })._retry || isAuthEndpoint(originalRequest.url) || isPublicRoute(pathname);
    if (shouldNotRetry) {
      return Promise.reject(error);
    }
    (originalRequest as {
      _retry?: boolean;
    })._retry = true;
    if (refreshTokenQueue.isInProgress()) {
      return new Promise((resolve, reject) => {
        refreshTokenQueue.enqueue({
          resolve: () => resolve(api(originalRequest)),
          reject
        });
      });
    }
    refreshTokenQueue.setRefreshing(true);
    try {
      await refresh();
      refreshTokenQueue.flush();
      return api(originalRequest);
    } catch (err_2) {
      await getLogoutUrl();
      refreshTokenQueue.flush(err_2);
      return Promise.reject(err_2);
    } finally {
      refreshTokenQueue.setRefreshing(false);
    }
  }), [refresh, getLogoutUrl, pathname]);
  useEffect(() => {
    refresh();
    const interceptor = createErrorInterceptor();
    return () => {
      api.interceptors.response.eject(interceptor);
    };
  }, [refresh, createErrorInterceptor]);
  const verifyCredentials = useCallback(async ({
    code,
    state
  }: VerifyCredentialsParams) => {
    setIsLoading(true);
    try {
      const {
        data: {
          partnerUser: partnerUser_0,
          isFirstLogin
        }
      } = await api.post<RefreshPartnerTokenResponse>(buildApiRoute(API_ROUTES.POST.AUTH.VERIFY_CREDENTIALS), {
        code,
        state
      });
      await updateUserSession(partnerUser_0);
      return {
        partnerUser: partnerUser_0,
        isFirstLogin
      };
    } catch (err_3) {
      setUserId(undefined);
      if (isAxiosError(err_3)) {
        throw err_3;
      }
      handleErrorTreatment(err_3);
      redirectToLogin(push);
    } finally {
      setIsLoading(false);
    }
  }, [push, updateUserSession]);
  return <AuthContext.Provider value={{
    isLoading,
    refresh,
    getLoginUrl,
    getLogoutUrl,
    verifyCredentials,
    userId,
    userRoles
  }}>
			{children}
		</AuthContext.Provider>;
};